import { useCallback, useEffect, useState } from "react";
import {
  browserSupportsWebAuthn,
  startRegistration,
} from "@simplewebauthn/browser";
import toastr from "toastr";
import { confirmAlert } from "react-confirm-alert";
import { AiOutlinePlusCircle } from "react-icons/ai";
import {
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";
import API from "../../../utils/API";
import AlertModal from "../../../utils/components/AlertModal";
import Button from "../../../utils/components/Button";
import Input from "../../../utils/components/Input";
import Modal from "../../../utils/components/Modal";
import timestampToDateFormat from "../../../utils/helpers/timestampToDateFormat";

const NAME_MAX_LENGTH = 100;
const PAGE_SIZE = 5;

const PasskeySettings = ({ translate }) => {
  const [supported] = useState(() => browserSupportsWebAuthn());
  const [passkeys, setPasskeys] = useState([]);
  // modal is null, "add" or "rename"
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);

  const totalPages = Math.max(Math.ceil(passkeys.length / PAGE_SIZE), 1);
  const visiblePasskeys = passkeys.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const loadPasskeys = useCallback(
    () =>
      API.getData("/auth/passkeys")
        .then(({ data: res }) => setPasskeys(res.data || []))
        .catch(() => {}),
    []
  );

  useEffect(() => {
    loadPasskeys();
  }, [loadPasskeys]);

  // a delete can remove the last card of the last page
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const closeModal = () => {
    setModal(null);
    setSelected(null);
    setName("");
  };

  const openAdd = () => {
    setName("");
    setSelected(null);
    setModal("add");
  };

  const openRename = (passkey) => {
    setName(passkey.name || "");
    setSelected(passkey);
    setModal("rename");
  };

  const handleAdd = async () => {
    if (busy) return;
    setBusy(true);

    let options;
    try {
      const { data: res } = await API.postData(
        "/auth/passkeys/registration_options"
      );
      options = res.data;
    } catch (error) {
      setBusy(false);
      return;
    }

    let credential;
    try {
      credential = await startRegistration({ optionsJSON: options });
    } catch (error) {
      setBusy(false);
      if (error.name === "NotAllowedError" || error.name === "AbortError") {
        toastr.info(translate("account.passkeys.cancelled"));
      } else if (error.name === "InvalidStateError") {
        toastr.warning(translate("account.passkeys.already_registered"));
      } else {
        toastr.error(translate("account.passkeys.error"));
      }
      return;
    }

    try {
      await API.postData("/auth/passkeys/registration", {
        credential,
        name: name.trim() || null,
      });
      toastr.success(translate("account.passkeys.added"));
      closeModal();
      // newest passkey comes first
      setPage(1);
      loadPasskeys();
    } catch (error) {
      // the API interceptor already showed the error
    }
    setBusy(false);
  };

  const handleRename = () => {
    const trimmed = name.trim();

    if (!trimmed) {
      toastr.warning(translate("account.passkeys.name_required"));
      return;
    }

    setBusy(true);
    API.patchData(`/auth/passkeys/${selected.id}`, { name: trimmed })
      .then(() => {
        toastr.success(translate("account.passkeys.renamed"));
        closeModal();
        loadPasskeys();
      })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  const handleDelete = (passkey) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <AlertModal
          message={translate("account.passkeys.delete_confirm")}
          buttons={[
            {
              label: translate("delete"),
              onClick: () => {
                API.deleteData(`/auth/passkeys/${passkey.id}`)
                  .then(() => {
                    toastr.success(translate("account.passkeys.deleted"));
                    loadPasskeys();
                  })
                  .catch(() => {})
                  .finally(onClose);
              },
              classNames: "btn-danger",
            },
            {
              label: translate("cancel"),
              onClick: onClose,
              classNames: "btn-secondary",
            },
          ]}
        />
      ),
    });
  };

  return (
    <div className="row passkey-settings">
      <div className="col-12 mt-2">
        <p className="fs-5 fw-bold pt-3 pb-1">
          {translate("account.settings.titles.passkeys")}
        </p>
      </div>
      <div className="col-12 d-flex align-items-center mb-3">
        <p className="pe-4 mb-0">
          {translate("account.settings.descriptions.passkeys")}
        </p>
        {passkeys.length > 0 && (
          <div className="ms-auto flex-shrink-0">
            {supported ? (
              <Button
                type="button"
                className="btn-m btn-blue d-flex align-items-center"
                onClick={openAdd}
              >
                <AiOutlinePlusCircle className="fs-4 me-1" />
                {translate("account.passkeys.add_button")}
              </Button>
            ) : (
              <small className="text-muted">
                {translate("account.passkeys.unsupported")}
              </small>
            )}
          </div>
        )}
      </div>
      <div className="col-12">
        {passkeys.length === 0 ? (
          <div className="no-passkeys text-center py-3 border rounded">
            <p className="text-muted">{translate("account.passkeys.empty")}</p>
            {supported ? (
              <Button
                type="button"
                className="btn-sm btn-blue text-uppercase px-4 py-1"
                onClick={openAdd}
              >
                <AiOutlinePlusCircle className="fs-4 me-1" />
                {translate("account.passkeys.add_button")}
              </Button>
            ) : (
              <small className="text-muted">
                {translate("account.passkeys.unsupported")}
              </small>
            )}
          </div>
        ) : (
          <>
            <div className="passkey-list border rounded">
              {visiblePasskeys.map((passkey) => (
                <div
                  key={passkey.id}
                  className="passkey-row d-flex align-items-center gap-3 px-3 py-2"
                >
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="fw-semibold text-truncate">
                      {passkey.name || translate("account.passkeys.unnamed")}
                    </div>
                    <small className="text-muted">
                      {translate("account.passkeys.created_at")}:{" "}
                      {timestampToDateFormat(passkey.created_at)}
                      {" · "}
                      {translate("account.passkeys.last_used")}:{" "}
                      {timestampToDateFormat(passkey.last_used_at) ||
                        translate("account.passkeys.never_used")}
                      {" · "}
                      {translate(
                        passkey.backed_up
                          ? "account.passkeys.synced"
                          : "account.passkeys.device_only"
                      )}
                    </small>
                  </div>
                  <div className="d-flex gap-1 flex-shrink-0">
                    <Button
                      type="button"
                      className="btn-orange text-white px-2 py-1 d-flex align-items-center"
                      onClick={() => openRename(passkey)}
                    >
                      <FiEdit2 />
                      <span className="visually-hidden">{translate("edit")}</span>
                    </Button>
                    <Button
                      type="button"
                      className="btn-secondary px-2 py-1 d-flex align-items-center"
                      onClick={() => handleDelete(passkey)}
                    >
                      <FiTrash2 />
                      <span className="visually-hidden">
                        {translate("delete")}
                      </span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="passkey-pagination d-flex align-items-center justify-content-between mt-3">
                <small className="text-muted">
                  {(page - 1) * PAGE_SIZE + 1}–
                  {Math.min(page * PAGE_SIZE, passkeys.length)} /{" "}
                  {passkeys.length}
                </small>
                <div className="d-flex gap-1">
                  <Button
                    type="button"
                    className="btn-secondary px-2 py-1 d-flex align-items-center"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    <FiChevronLeft />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (pageNumber) => (
                      <Button
                        key={pageNumber}
                        type="button"
                        className={`px-2 py-1 ${
                          pageNumber === page
                            ? "btn-orange text-white"
                            : "btn-secondary"
                        }`}
                        onClick={() => setPage(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    )
                  )}
                  <Button
                    type="button"
                    className="btn-secondary px-2 py-1 d-flex align-items-center"
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    <FiChevronRight />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <Modal
        title={translate(
          modal === "rename"
            ? "account.passkeys.rename_modal_title"
            : "account.passkeys.add_modal_title"
        )}
        positiveButtonNode={
          modal === "rename"
            ? translate("save")
            : translate("account.passkeys.create")
        }
        negativeButtonNode={translate("cancel")}
        positiveButtonHandler={modal === "rename" ? handleRename : handleAdd}
        modalSize="md"
        show={modal !== null}
        setShow={(show) => !show && closeModal()}
        disableButtons={busy}
      >
        <Input
          title={translate("account.passkeys.name_label")}
          placeholder={translate("account.passkeys.name_placeholder")}
          name="passkeyName"
          maxLength={NAME_MAX_LENGTH}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default PasskeySettings;
